import { format, isValid, parseISO } from 'date-fns';

import {
    ActivityLogChange,
    ActivityLogModelType,
    ActivtyLog,
    AuditTrailFilterValue,
    getModelName,
    isActivityLogChange,
    AuditTrailTimeframeOptions,
} from '../../models/activty-log.model';

import { AuditRefs, EMPTY_AUDIT_REFS } from './references';

const ISO_TIMESTAMP = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;
const HAS_OFFSET = /(Z|[+-]\d{2}:?\d{2})$/;

/**
 * composer-backend stores and returns naive UTC timestamps (no offset), which
 * parseISO would read as local time. Treat offset-less values as UTC.
 */
export const parseBackendDate = (value: string): Date =>
    parseISO(HAS_OFFSET.test(value) ? value : `${value}Z`);
export const isStructuredValue = (value: unknown): boolean =>
    value !== null && typeof value === 'object';
export const formatAuditValue = (value: unknown): string => {
    if (value === null || value === undefined) return '—';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (isStructuredValue(value)) return JSON.stringify(value, null, 2);
    if (typeof value === 'string' && ISO_TIMESTAMP.test(value)) {
        // Free text can look like a timestamp; only format real dates.
        const parsed = parseBackendDate(value);
        return isValid(parsed) ? format(parsed, 'dd MMM yyyy, hh:mm a') : value;
    }
    return String(value);
};

export type DiffRow = {
    field: string;
    before?: unknown;
    after?: unknown;
    value?: unknown;
};

const snapshotRows = (snapshot: Record<string, unknown>): DiffRow[] =>
    Object.keys(snapshot)
        .sort()
        .map((field) => ({ field, value: snapshot[field] }));
export const getDiffRows = (
    change: ActivityLogChange,
): { mode: 'diff' | 'snapshot'; rows: DiffRow[] } => {
    if (change.before == null) {
        return { mode: 'snapshot', rows: snapshotRows(change.after ?? {}) };
    }
    if (change.after == null) {
        return { mode: 'snapshot', rows: snapshotRows(change.before) };
    }
    const { before, after } = change;
    return {
        mode: 'diff',
        rows: change.changed_fields.map((field) => ({
            field,
            before: before[field],
            after: after[field],
        })),
    };
};

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

export const getDiffToggleLabel = (change: ActivityLogChange): string => {
    const { mode, rows } = getDiffRows(change);
    const parts = change.parts?.length ?? 0;
    if (mode === 'snapshot' && rows.length > 0) return 'View details';
    const fields = mode === 'diff' ? rows.length : 0;
    if (parts === 0) return `${plural(fields, 'field')} changed`;
    return fields > 0
        ? `${plural(fields, 'field')} and ${plural(parts, 'part')} changed`
        : `${plural(parts, 'part')} changed`;
};

/** Large values come back as an opaque digest, fetched separately on request. */
export const isOpaqueValue = (value: unknown): value is string =>
    typeof value === 'string' && value.startsWith('sha256:');

type SlateNode = { text?: unknown; children?: SlateNode[] };

const nodeText = (node: SlateNode): string =>
    typeof node.text === 'string'
        ? node.text
        : (node.children ?? []).map(nodeText).join('');

/**
 * A message body (Slate document) as one line per block, so two versions can
 * be diffed line by line. Anything else falls back to pretty JSON lines.
 */
export const structureToLines = (value: unknown): string[] => {
    if (
        Array.isArray(value) &&
        value.every((n) => n && typeof n === 'object' && 'children' in n)
    ) {
        return (value as SlateNode[])
            .map((node) => nodeText(node).trim())
            .filter(Boolean);
    }
    return JSON.stringify(value, null, 2).split('\n');
};

export const timeframeToStart = (
    timeframe: AuditTrailTimeframeOptions,
    now: Date = new Date(),
): Date | undefined => {
    if (timeframe === AuditTrailTimeframeOptions.LIFETIME) return undefined;
    const start = new Date(now);
    start.setDate(
        now.getDate() -
            (timeframe === AuditTrailTimeframeOptions.WEEK ? 7 : 30),
    );
    return start;
};

export const filterLogs = (
    logs: ActivtyLog[],
    filters: AuditTrailFilterValue,
    now: Date = new Date(),
): ActivtyLog[] => {
    const start = timeframeToStart(filters.timeframe, now);
    return logs.filter(
        (log) =>
            (filters.person === 'all' ||
                String(log.user_id) === filters.person) &&
            (!start || parseBackendDate(log.created_at) >= start),
    );
};

export const groupLogsByDay = (
    logs: ActivtyLog[],
): { sortedDates: string[]; groupedLogs: Record<string, ActivtyLog[]> } => {
    const groupedLogs = logs.reduce((acc, log) => {
        const key = format(parseBackendDate(log.created_at), 'dd MMMM yyyy');
        (acc[key] ??= []).push(log);
        return acc;
    }, {} as Record<string, ActivtyLog[]>);
    const sortedDates = Object.keys(groupedLogs).sort(
        (a, b) => new Date(b).getTime() - new Date(a).getTime(),
    );
    return { sortedDates, groupedLogs };
};

const snapshotName = (log: ActivtyLog): string | undefined => {
    if (!isActivityLogChange(log.data)) return undefined;
    const snapshot = log.data.after ?? log.data.before;
    const name = snapshot?.name ?? snapshot?.label_name;
    return typeof name === 'string' && name.trim() ? name : undefined;
};

/**
 * "{Type}: {name}" for the object an entry belongs to (its target, else
 * itself). Names come from the backend when it sends them, else from the
 * row's own snapshot, and only when that snapshot is the object being named.
 * Raw ids (often UUIDs) are never shown.
 */
export const getTargetLabel = (
    log: ActivtyLog,
    refs: AuditRefs = EMPTY_AUDIT_REFS,
): string => {
    const hasTarget = Boolean(log.target_type && log.target_id != null);
    const type = getModelName(hasTarget ? log.target_type : log.object_type);
    const isOwnRow =
        !hasTarget ||
        (log.target_type === log.object_type &&
            String(log.target_id) === String(log.object_id));
    const refName =
        hasTarget && log.target_type === ActivityLogModelType.COMPOUND_TRIGGER
            ? refs.triggerNames.get(String(log.target_id))
            : undefined;
    const name = hasTarget
        ? log.target_name ??
          refName ??
          (isOwnRow ? log.object_name ?? snapshotName(log) : undefined)
        : log.object_name ?? snapshotName(log);
    return name ? `${type}: ${name}` : type;
};
